import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft, FileText, RotateCcwClock, Printer, ChevronDown, ChevronUp, LoaderCircle, Save, Check, CircleCheck, X, User,
} from "lucide-react";
import {
  useVisit, useInvestigations, usePublicAppSettings, useSlotBlocks, usePatientPhotoBlob, usePatientVisits, useDocuments, type EmrNote, type PrescriptionRow, type Icd10Code,
  type VisitAggregate, type MedicineRow, type VitalRow,
} from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  VisitDetailsColumn, CodesPlanColumn, InvestigationsColumn, ReviewBar,
} from "@/features/emr/VisitColumns";
import { ReviewDateDialog } from "@/features/emr/ReviewDateDialog";
import { PrescriptionTable } from "@/features/emr/PrescriptionTable";
import {
  PreviousVisitsDrawer, type CopySelection,
} from "@/features/emr/PreviousVisitsDrawer";
import { DocumentViewer } from "@/features/emr/DocumentViewer";
import { createPrescriptionPdf, type PrescriptionMedicine } from "@/lib/pdf/prescriptionPdf";
import {
  createCombinedInvestigationPdf, classifyInvestigation, type InvestigationCategory,
} from "@/lib/pdf/investigationPdf";
import { createVisitSummaryPdf } from "@/lib/pdf/visitSummaryPdf";
import { printPdf } from "@/lib/print";
import { printCompositionPref } from "@/lib/rxPrint";
import { PatientPhotoViewerDialog } from "@/features/patients/PatientPhotoViewerDialog";
import { apiFetch, getJwt, API_URL } from "@/lib/api";
// fmtDate = DD/MM/YYYY (on-screen default) · fmtDatePdf = DD-MMM-YYYY —
// on-screen EXCEPTION for the header strip's dates (2026-11-03, post-mortem
// #302); everything else on-screen keeps fmtDate. dateIST = IST day string.
import { fmtDate, dateIST, fmtDatePdf } from "@emr/shared/india";
import { computeAgeYm, formatAgeLabel } from "@emr/shared/age";
import { dayUnavailableReason, DEFAULT_WEEKLY_SCHEDULE } from "@emr/shared/scheduling";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

interface VitalsFormState {
  tempC: string;
  bpSystolic: string;
  bpDiastolic: string;
  pulse: string;
  respRate: string;
  spO2: string;
  heightCm: string;
  weightKg: string;
}

type VitalKey = keyof VitalsFormState;

const EMPTY_VITALS: VitalsFormState = {
  tempC: "", bpSystolic: "", bpDiastolic: "", pulse: "",
  respRate: "", spO2: "", heightCm: "", weightKg: "",
};

// Integer-only vitals — BP, Pulse, Resp, SpO₂, Height never accept decimals.
// Temp and Weight allow decimals (parseFloat in vitalValue).
const INTEGER_VITAL_KEYS: Set<VitalKey> = new Set([
  "bpSystolic", "bpDiastolic", "pulse", "respRate", "spO2", "heightCm",
]);

// Render-mirror of the four autosave payloads for the unload flush (Batch 6 / H-2,
// 2026-11-01). The flush effect's deps ([id, canEdit*]) exclude every payload input,
// so its closure used to freeze the payload functions at the mount render — the
// flush either no-op'd (notes/review) or POSTed pre-hydration empties over saved
// baselines (investigations/vitals — both endpoints upsert → data wiped). The mirror
// effect below re-writes this ref after EVERY render, so the flush always reads
// live payloads; the lastSaved* baselines it diffs against are already refs.
type FlushPayloads = {
  notesJson: string;
  // Total-wipe signature of the current notes payload (see isTotalWipe).
  notesWipe: boolean;
  invJson: string;
  // Total-wipe signature of the current investigations payload (see isInvTotalWipe).
  invWipe: boolean;
  vitalsJson: string;
  reviewJson: string;
  // Current "nothing to save" review state: !reviewNeeded && !reviewDate.
  reviewSkip: boolean;
};

export default function EmrVisitPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isAdmin, permissions } = useAuth();
  const { data, isLoading, refetch } = useVisit(id);
  const { data: invData } = useInvestigations();
  const investigations = invData?.rows ?? [];
  // Previous visits list — feeds the count badge on the "Previous visits"
  // button. The drawer fetches its own copy; this is just for the count.
  const { data: prevVisitsData } = usePatientVisits(data?.visit.patientId);
  // Documents list — feeds the count badge on the "Documents" button. Same
  // patient-scoped query the Documents dialog uses (["documents", patientId,
  // "all"]), so uploads/deletes inside the dialog keep the badge in sync.
  const { data: docsData } = useDocuments(data?.visit.patientId);

  const [docsOpen, setDocsOpen] = useState(false);
  const [prevOpen, setPrevOpen] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [printing, setPrinting] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  // Review-date dialog shown when Print Prescription / Print Visit Summary is
  // clicked but the visit has no review date selected (reviewNeeded=false &&
  // reviewDate empty). printMode records which print triggered the dialog so
  // Save & Print knows which PDF to build after persisting the review.
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewDialogBusy, setReviewDialogBusy] = useState(false);
  const [reviewDialogPrintMode, setReviewDialogPrintMode] = useState<"rx" | "summary">("rx");
  // Live prescription rows reported by PrescriptionTable (used by page-level
  // prints so they don't read a cached aggregate that lags in-flight rows).
  const [liveMedicines, setLiveMedicines] = useState<MedicineRow[]>([]);
  const [autosaveState, setAutosaveState] = useState<"idle" | "saving" | "saved">("idle");
  // Bumped by refetchAndHydrate() after an explicit refetch (global Save /
  // copy-from-previous) so the PrescriptionTable re-adopts server medicines.
  const [hydrateSignal, setHydrateSignal] = useState(0);

  // ---- Lifted visit-details state ----
  const [subjective, setSubjective] = useState("");
  const [objective, setObjective] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [plan, setPlan] = useState("");
  const [noteText, setNoteText] = useState("");
  const [icd10Codes, setIcd10Codes] = useState<Icd10Code[]>([]);

  // ---- Lifted vitals state ----
  const [vitalsForm, setVitalsForm] = useState<VitalsFormState>(EMPTY_VITALS);

  // ---- Header photo (thumbnail; viewer handles the big display + change) ----
  const patientPhotoKey = data?.visit.patientPhotoR2Key ?? null;
  const photoQuery = usePatientPhotoBlob(data?.visit.patientId, patientPhotoKey);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!photoQuery.data) { setPhotoUrl(null); return; }
    const url = URL.createObjectURL(photoQuery.data);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoQuery.data]);

  // ---- Lifted investigations state ----
  const [selectedInv, setSelectedInv] = useState<string[]>([]);
  const [labNotes, setLabNotes] = useState("");
  const [radiologyNotes, setRadiologyNotes] = useState("");
  const [electrophysiologyNotes, setElectrophysiologyNotes] = useState("");
  // Per-investigation Region & Side / Clinical Notes for Rad/EP tests.
  // Map of investigationId → { regionAndSide, clinicalNotes }.
  const [investigationDetails, setInvestigationDetails] = useState<Record<string, { regionAndSide: string; clinicalNotes: string }>>({});

  // ---- Lifted review state ----
  const [reviewNeeded, setReviewNeeded] = useState(false);
  const [reviewDate, setReviewDate] = useState("");

  // ---- Review-date usability (blocked / off-day guard for printing) ----
  // The ReviewBar shows an inline red warning when the picked review date is
  // unusable, and the server (PATCH /visits/:id/review) rejects the save with
  // 422 review_date_not_working — but the print entrypoints used to only
  // check "is a date set", so a blocked date still printed on the PDF. This
  // computed value lets every print path treat "date set but unusable" the
  // same as "no date set" (prompt via ReviewDateDialog instead of printing).
  const { data: settingsData } = usePublicAppSettings();
  const { data: blocksData } = useSlotBlocks();
  const weeklySchedule = (settingsData?.weeklySchedule as never) ?? DEFAULT_WEEKLY_SCHEDULE;
  const slotBlocks = (blocksData?.rows ?? []).map((b) => ({
    date: typeof b.date === "string" ? b.date : dateIST(b.date as unknown as string),
    slot: b.slot,
    maxOverride: b.maxOverride,
    startOverride: b.startOverride,
    reason: b.reason,
  }));
  const reviewDateUnavailable =
    !reviewNeeded && reviewDate
      ? dayUnavailableReason(new Date(reviewDate + "T00:00:00"), weeklySchedule, slotBlocks)
      : null;

  const canView = isAdmin || permissions.has("emr.view");
  const canEdit = isAdmin || permissions.has("emr.edit");

  // ---- Hydration ----
  // Local state is hydrated from the server aggregate when the visit changes
  // (first load, navigation to another visit) and on explicit
  // refetchAndHydrate calls (global Save, copy-from-previous). Background
  // refetches — e.g. the prescription save invalidating ["visits", id], or
  // window-focus refetches — must NOT re-run hydration: they used to silently
  // wipe unsaved local edits (investigation selections, review changes,
  // notes typed within the autosave debounce) every time a drug was added to
  // the prescription.
  const hydratedVisitId = useRef<string | undefined>(undefined);

  const hydrate = (d: VisitAggregate) => {
    hydratedVisitId.current = d.visit.id;
    const note = d.note;
    setSubjective(note?.subjective ?? "");
    setObjective(note?.objective ?? "");
    setDiagnosis(note?.diagnosis ?? "");
    setPlan(note?.plan ?? "");
    setNoteText(note?.note ?? "");
    setIcd10Codes(note?.icd10Codes ?? []);

    const latest = d.vitals[0];
    // Height is integer-only now — truncate legacy decimal values (rows saved
    // before the integer rule) so the displayed value always matches what a
    // save would persist.
    setVitalsForm(latest ? {
      tempC: latest.tempC?.toString() ?? "",
      bpSystolic: latest.bpSystolic?.toString() ?? "",
      bpDiastolic: latest.bpDiastolic?.toString() ?? "",
      pulse: latest.pulse?.toString() ?? "",
      respRate: latest.respRate?.toString() ?? "",
      spO2: latest.spO2?.toString() ?? "",
      heightCm: latest.heightCm != null ? String(Math.trunc(latest.heightCm)) : "",
      weightKg: latest.weightKg?.toString() ?? "",
    } : EMPTY_VITALS);

    const invReq = d.investigationRequest;
    setSelectedInv(invReq?.selectedInvestigationIds ?? []);
    setLabNotes(invReq?.notes ?? "");
    setRadiologyNotes(invReq?.radiologyNotes ?? "");
    setElectrophysiologyNotes(invReq?.electrophysiologyNotes ?? "");
    // Hydrate per-investigation details, converting nulls to empty strings
    // for the UI (the API stores null for absent values; the UI uses "").
    const rawDetails = invReq?.investigationDetails ?? {};
    const details: Record<string, { regionAndSide: string; clinicalNotes: string }> = {};
    for (const [invId, d] of Object.entries(rawDetails)) {
      if (d && (d.regionAndSide || d.clinicalNotes)) {
        details[invId] = {
          regionAndSide: d.regionAndSide ?? "",
          clinicalNotes: d.clinicalNotes ?? "",
        };
      }
    }
    setInvestigationDetails(details);

    setReviewNeeded(d.visit.reviewNeeded);
    setReviewDate(d.visit.reviewDate ? dateIST(d.visit.reviewDate) : "");

    // Seed the autosave baselines with the server snapshot (mapped through
    // the same payload transforms) so hydration itself never schedules a
    // redundant save.
    lastSavedPayload.current = JSON.stringify({
      subjective: note?.subjective || null,
      objective: note?.objective || null,
      diagnosis: note?.diagnosis || null,
      plan: note?.plan || null,
      note: note?.note || null,
      icd10Codes: note?.icd10Codes ?? [],
    });
    lastSavedInvPayload.current = JSON.stringify({
      selectedInvestigationIds: invReq?.selectedInvestigationIds ?? [],
      // NOTE: seeded from the note's diagnosis (what local state holds after
      // hydration), not invReq.diagnosis — the API mirrors the note diagnosis
      // into the request on save, so both are equal server-side, but the
      // local `diagnosis` state is hydrated from the note.
      diagnosis: note?.diagnosis || null,
      notes: invReq?.notes || null,
      radiologyNotes: invReq?.radiologyNotes || null,
      electrophysiologyNotes: invReq?.electrophysiologyNotes || null,
      investigationDetails: Object.fromEntries(
        Object.entries(invReq?.investigationDetails ?? {})
          .filter(([, v]) => v && (v.regionAndSide || v.clinicalNotes))
          .map(([k, v]) => [k, {
            regionAndSide: v!.regionAndSide?.trim() || null,
            clinicalNotes: v!.clinicalNotes?.trim() || null,
          }]),
      ),
    });
    // Seed the vitals baseline from the server snapshot so hydration itself
    // doesn't trigger a redundant vitals save. Computed through the same
    // transforms as vitalsPayload() reads the form (height truncated to
    // integer, BMI derived from h/w) so a legacy decimal height doesn't
    // register as an edit on load.
    const v = latest ? (() => {
      const h = latest.heightCm != null ? Math.trunc(latest.heightCm) : null;
      const w = latest.weightKg ?? null;
      return {
        tempC: latest.tempC ?? null,
        bpSystolic: latest.bpSystolic ?? null,
        bpDiastolic: latest.bpDiastolic ?? null,
        pulse: latest.pulse ?? null,
        respRate: latest.respRate ?? null,
        spO2: latest.spO2 ?? null,
        heightCm: h,
        weightKg: w,
        bmi: h && w && h > 0 && w > 0 ? parseFloat((w / ((h / 100) * (h / 100))).toFixed(1)) : null,
      };
    })() : {
      tempC: null, bpSystolic: null, bpDiastolic: null, pulse: null,
      respRate: null, spO2: null, heightCm: null, weightKg: null, bmi: null,
    };
    lastSavedVitalsPayload.current = JSON.stringify(v);
    // Seed the review baseline from the server snapshot, using the same
    // conversion as reviewPayload() (dateIST → ISO) so hydration doesn't
    // trigger a redundant review autosave.
    lastSavedReviewPayload.current = JSON.stringify({
      reviewNeeded: d.visit.reviewNeeded,
      reviewDate: d.visit.reviewDate
        ? new Date(dateIST(d.visit.reviewDate)).toISOString()
        : null,
    });
  };

  useEffect(() => {
    if (!data) return;
    // Hydrate only when the visit itself changes (first load or navigating
    // to a different visit in the same component instance). Background
    // refetches of the SAME visit are echoes of our own writes (or stale
    // snapshots) — re-hydrating from them wipes unsaved local edits.
    if (hydratedVisitId.current === data.visit.id) return;
    hydrate(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Explicit re-hydration after actions that changed the server state
  // (global Save, copy-from-previous). Bumps hydrateSignal so the
  // PrescriptionTable re-adopts the server medicines.
  const refetchAndHydrate = async () => {
    const res = await refetch();
    if (res.data) {
      hydrate(res.data);
      setHydrateSignal((s) => s + 1);
    }
  };

  // ---- Autosave: 1s debounce on visit-details notes, investigations, vitals,
  //      and review. Each has its own debounce timer and last-saved baseline
  //      so independent fields don't trigger redundant saves of each other. ----
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vitalsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // lastSaved* refs are write-only baselines for the autosave diff (updated in
  // event/async handlers, never read during render). react-hooks/immutability
  // flags each write; these are deliberate — refs avoid re-render loops that
  // state would cause on every save. See eslint.config.js triage 2026-10-04.
  const lastSavedPayload = useRef("");
  const lastSavedInvPayload = useRef("");
  const lastSavedVitalsPayload = useRef("");
  const lastSavedReviewPayload = useRef("");
  // One flush per unload — pagehide + beforeunload both fire (see flush effect).
  const flushDoneRef = useRef(false);
  // Latest-render mirror of the flush payloads — see FlushPayloads above.
  const flushPayloadsRef = useRef<FlushPayloads | null>(null);

  const notesPayload = () => ({
    subjective: subjective || null,
    objective: objective || null,
    diagnosis: diagnosis || null,
    plan: plan || null,
    note: noteText || null,
    icd10Codes,
  });

  // Total-wipe guard (2026-10-05 — see post-mortem.md): the 2026-10-04 data-
  // loss incident was an autosave POST whose text fields were ALL null while
  // icd10Codes stayed populated (stale local state between :36 and :43). No
  // legitimate user action empties all five SOAP fields simultaneously — a
  // user clearing one field still leaves the other four. If local state ever
  // collapses to "all texts empty", REFUSE to send and keep the last saved
  // payload as the baseline (so the autosave doesn't retry in a loop). The
  // explicit Save/Complete buttons use a server-merge instead (they must
  // still be able to save a genuinely blank new note for a fresh visit, and
  // a wiped NEW note has nothing to lose — the guard applies to autosaves of
  // visits that already have saved content, tracked by lastSavedPayload).
  const isTotalWipe = (p: ReturnType<typeof notesPayload>) =>
    p.subjective === null && p.objective === null && p.diagnosis === null &&
    p.plan === null && p.note === null;

  // Investigations total-wipe guard (2026-11-01, Batch 6 / H-2 — mirrors the notes
  // guard above): the investigations endpoint upserts, so a frozen/empty payload
  // would wipe a saved investigation request. No legitimate user action empties
  // selections AND all three category notes AND per-test details AND the mirrored
  // diagnosis simultaneously. Diagnosis is INCLUDED so a visit with a real (non-null)
  // local diagnosis never trips the guard — the suspicious all-null signature matches
  // the 2026-10-04 incident (post-mortem #245).
  const isInvTotalWipe = (p: ReturnType<typeof investigationsPayload>) =>
    p.selectedInvestigationIds.length === 0 && p.notes === null &&
    p.radiologyNotes === null && p.electrophysiologyNotes === null &&
    Object.keys(p.investigationDetails).length === 0 && p.diagnosis === null;

  // Does the last-saved investigations baseline carry any content at all?
  // (selected tests, any category note, per-test details, or a mirrored diagnosis).
  // Unparseable baseline → treat as content (block the send — safe direction).
  const invBaselineHasContent = (baselineJson: string): boolean => {
    if (!baselineJson) return false;
    try {
      const b = JSON.parse(baselineJson) as {
        selectedInvestigationIds?: unknown[];
        notes?: string | null;
        radiologyNotes?: string | null;
        electrophysiologyNotes?: string | null;
        investigationDetails?: Record<string, unknown>;
        diagnosis?: string | null;
      };
      return (b.selectedInvestigationIds?.length ?? 0) > 0
        || b.notes != null || b.radiologyNotes != null || b.electrophysiologyNotes != null
        || Object.keys(b.investigationDetails ?? {}).length > 0
        || b.diagnosis != null;
    } catch {
      return true;
    }
  };

  const investigationsPayload = () => ({
    selectedInvestigationIds: selectedInv,
    diagnosis: diagnosis || null,
    notes: labNotes || null,
    radiologyNotes: radiologyNotes || null,
    electrophysiologyNotes: electrophysiologyNotes || null,
    investigationDetails: Object.fromEntries(
      Object.entries(investigationDetails)
        .filter(([, v]) => v.regionAndSide.trim() || v.clinicalNotes.trim())
        .map(([k, v]) => [k, {
          regionAndSide: v.regionAndSide.trim() || null,
          clinicalNotes: v.clinicalNotes.trim() || null,
        }]),
    ),
  });

  // Vitals payload — empty fields are sent as null (blank-by-default entry).
  const vitalValue = (k: VitalKey): number | null => {
    if (!vitalsForm[k]) return null;
    return k === "tempC" || k === "weightKg"
      ? parseFloat(vitalsForm[k])
      : parseInt(vitalsForm[k], 10);
  };

  const vitalsPayload = () => {
    const h = parseFloat(vitalsForm.heightCm);
    const w = parseFloat(vitalsForm.weightKg);
    const bmi = h > 0 && w > 0 ? (w / ((h / 100) * (h / 100))) : null;
    return JSON.stringify({
      tempC: vitalValue("tempC"),
      bpSystolic: vitalValue("bpSystolic"),
      bpDiastolic: vitalValue("bpDiastolic"),
      pulse: vitalValue("pulse"),
      respRate: vitalValue("respRate"),
      spO2: vitalValue("spO2"),
      heightCm: vitalValue("heightCm"),
      weightKg: vitalValue("weightKg"),
      bmi: bmi ? parseFloat(bmi.toFixed(1)) : null,
    });
  };

  // Review payload — reviewNeeded (PRN flag) + reviewDate (ISO or null).
  const reviewPayload = () => JSON.stringify({
    reviewNeeded,
    reviewDate: reviewDate ? new Date(reviewDate).toISOString() : null,
  });

  // Render mirror for the unload flush (see FlushPayloads at the top). Runs after
  // EVERY render (no deps) so flushPayloadsRef.current always holds the latest
  // payloads. The flush reads the ref at event time — this is what unfreezes the
  // pagehide/beforeunload path from the mount-render closure (Batch 6 / H-2,
  // 2026-11-01).
  useEffect(() => {
    const notesP = notesPayload();
    const invP = investigationsPayload();
    flushPayloadsRef.current = {
      notesJson: JSON.stringify(notesP),
      notesWipe: isTotalWipe(notesP),
      invJson: JSON.stringify(invP),
      invWipe: isInvTotalWipe(invP),
      vitalsJson: vitalsPayload(),
      reviewJson: reviewPayload(),
      reviewSkip: !reviewNeeded && !reviewDate,
    };
  });

  const autosaveNotes = async () => {
    const payload = JSON.stringify(notesPayload());
    // Total-wipe guard: if the visit already HAS saved note content (the
    // baseline payload carries any text) and local state collapsed to
    // all-null texts, refuse to send — this state can only come from stale
    // local data, not a user action. Reset the baseline to the safe payload
    // so this state doesn't retrigger forever.
    const baselineHadText = /"(subjective|objective|diagnosis|plan|note)":"[^"]+"/.test(lastSavedPayload.current);
    if (isTotalWipe(notesPayload()) && baselineHadText) {
      // eslint-disable-next-line react-hooks/immutability -- deliberate: reset baseline to block the wiped state from re-sending
      lastSavedPayload.current = payload;
      return;
    }
    setAutosaveState("saving");
    try {
      await apiFetch(`/visits/${id}/notes`, { method: "POST", body: payload });
      lastSavedPayload.current = payload;
      setAutosaveState("saved");
      setTimeout(() => setAutosaveState("idle"), 1500);
    } catch (e) {
      setAutosaveState("idle");
      const err = e as Error & { status?: number };
      // 401 is already surfaced by apiFetch's notifyUnauthorized; don't double-toast.
      if (err?.status !== 401) toast.error("Failed to autosave notes");
    }
  };

  const autosaveInvestigations = async () => {
    setAutosaveState("saving");
    try {
      const payload = JSON.stringify(investigationsPayload());
      await apiFetch(`/visits/${id}/investigations`, { method: "POST", body: payload });
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedInvPayload.current = payload;
      setAutosaveState("saved");
      setTimeout(() => setAutosaveState("idle"), 1500);
    } catch (e) {
      setAutosaveState("idle");
      const err = e as Error & { status?: number };
      if (err?.status !== 401) toast.error("Failed to autosave investigations");
    }
  };

  const autosaveVitals = async () => {
    // Baseline check (2026-10-21, post-mortem #245): the Save/Complete flush
    // calls this whenever the debounce ref is non-null, but the ref stays
    // stale after the timer has already fired — without this check every
    // explicit Save after a vitals edit inserted a duplicate row.
    const payload = vitalsPayload();
    if (payload === lastSavedVitalsPayload.current) return;
    setAutosaveState("saving");
    try {
      await apiFetch(`/visits/${id}/vitals`, { method: "POST", body: payload });
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedVitalsPayload.current = payload;
      setAutosaveState("saved");
      setTimeout(() => setAutosaveState("idle"), 1500);
    } catch (e) {
      setAutosaveState("idle");
      const err = e as Error & { status?: number };
      if (err?.status !== 401) toast.error("Failed to autosave vitals");
    }
  };

  const autosaveReview = async () => {
    setAutosaveState("saving");
    try {
      const payload = reviewPayload();
      await apiFetch(`/visits/${id}/review`, { method: "PATCH", body: payload });
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedReviewPayload.current = payload;
      setAutosaveState("saved");
      setTimeout(() => setAutosaveState("idle"), 1500);
    } catch (e) {
      setAutosaveState("idle");
      const err = e as Error & { status?: number };
      if (err?.status !== 401) {
        // Don't toast on review_date_required — that's a validation guard,
        // not a transient error. The user will see it on explicit Save.
        const body = (err as Error & { body?: { error?: string } })?.body;
        if (body?.error !== "review_date_required" && body?.error !== "review_date_not_working") {
          toast.error("Failed to autosave review");
        }
      }
    }
  };

  // Notes autosave (1s debounce) — gated on emr.edit. Skips until the first
  // hydration lands (mount state is empty defaults, not a user edit).
  useEffect(() => {
    if (hydratedVisitId.current !== id) return;
    if (!canEdit) return;
    // Skip redundant saves triggered by refetch hydration (same content, new array refs).
    if (JSON.stringify(notesPayload()) === lastSavedPayload.current) return;
    setAutosaveState("idle");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void autosaveNotes();
    }, 1000);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjective, objective, diagnosis, plan, noteText, icd10Codes]);

  // Investigations autosave (1s debounce) — gated on investigations.create
  // (the POST /visits/:id/investigations permission). Covers the selected
  // tests, all three category notes, and the diagnosis (mirrored into the
  // investigation request by the API). Also skips until first hydration.
  const canEditInvestigations = isAdmin || permissions.has("investigations.create");
  useEffect(() => {
    if (hydratedVisitId.current !== id) return;
    if (!canEditInvestigations) return;
    // Skip redundant saves when the payload already matches the server
    // snapshot (hydration seeds lastSavedInvPayload).
    if (JSON.stringify(investigationsPayload()) === lastSavedInvPayload.current) return;
    setAutosaveState("idle");
    if (invDebounceRef.current) clearTimeout(invDebounceRef.current);
    invDebounceRef.current = setTimeout(() => {
      void autosaveInvestigations();
    }, 1000);
    return () => {
      if (invDebounceRef.current) clearTimeout(invDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInv, labNotes, radiologyNotes, electrophysiologyNotes, investigationDetails, diagnosis]);

  // Vitals autosave (1s debounce) — gated on vitals.create. Skips until
  // first hydration. POST /visits/:id/vitals UPSERTS the visit's single
  // vitals row (2026-10-27, post-mortem #256 — was insert-only, which
  // accumulated 5-7 rows per visit), so we send the full vitals set each
  // time. The dedup baseline prevents redundant saves when nothing changed.
  const canEditVitals = isAdmin || permissions.has("vitals.create");
  useEffect(() => {
    if (hydratedVisitId.current !== id) return;
    if (!canEditVitals) return;
    if (vitalsPayload() === lastSavedVitalsPayload.current) return;
    setAutosaveState("idle");
    if (vitalsDebounceRef.current) clearTimeout(vitalsDebounceRef.current);
    vitalsDebounceRef.current = setTimeout(() => {
      vitalsDebounceRef.current = null; // fired — no longer pending (flush checks this)
      void autosaveVitals();
    }, 1000);
    return () => {
      if (vitalsDebounceRef.current) clearTimeout(vitalsDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vitalsForm]);

  // Review autosave (1s debounce) — gated on emr.edit. Skips until first
  // hydration. Saves reviewNeeded + reviewDate. Skips if the review date
  // is invalid (reviewNeeded=false && reviewDate empty) — the server rejects
  // that combination with 422 review_date_required, so there's no point
  // sending it. The user will be prompted to pick a date on Save/Complete.
  useEffect(() => {
    if (hydratedVisitId.current !== id) return;
    if (!canEdit) return;
    if (!reviewNeeded && !reviewDate) return; // nothing to save yet
    if (reviewPayload() === lastSavedReviewPayload.current) return;
    setAutosaveState("idle");
    if (reviewDebounceRef.current) clearTimeout(reviewDebounceRef.current);
    reviewDebounceRef.current = setTimeout(() => {
      void autosaveReview();
    }, 1000);
    return () => {
      if (reviewDebounceRef.current) clearTimeout(reviewDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewNeeded, reviewDate]);

  // ---- pagehide autosave flush (PWA-reload protection, 2026-09-24) ------
  // The PWA force-update policy reloads the page on controllerchange; a
  // reload within the 1s autosave debounce window would drop the pending
  // buffer (the debounced POST never fires). On pagehide/beforeunload we
  // cancel any pending debounce and fire all four saves immediately using
  // keepalive fetches (which the browser completes even as the page unloads).
  // Payloads come from the render mirror (flushPayloadsRef, re-written after
  // every render — see FlushPayloads at the top) + the live lastSaved* baseline
  // refs, NOT from this effect's closure: its deps ([id, canEdit*]) exclude
  // every payload input, so the closure used to freeze the payload functions
  // at the mount render (2026-11-01, Batch 6 / H-2) — the flush either no-op'd
  // (notes/review) or POSTed pre-hydration empties over saved baselines
  // (investigations/vitals — both endpoints upsert → data wiped). The flush is
  // hydration-gated (nothing real exists before the first hydrate()) and
  // one-shot per unload (flushDoneRef — pagehide + beforeunload both fire;
  // 2026-10-21, post-mortem #245). The investigations total-wipe guard mirrors
  // the notes guard (isInvTotalWipe): a frozen-empty payload must never
  // upsert-wipe a saved request. Payload-dedup guards mirror the autosave
  // effects: nothing is sent unless it actually differs from the last saved
  // payload. Best-effort by design — if the flush fetch fails, the data is
  // lost exactly as it would have been without this listener; it can never
  // make things worse or double-save.
  useEffect(() => {
    const flushPendingSaves = () => {
      // pagehide AND beforeunload both fire on unload (Chrome fires both).
      // Without a re-entrancy guard every pending diff is posted twice —
      // the flush never updates the baselines, so the second event re-sends
      // (2026-10-21, post-mortem #245: two identical all-null rows at the
      // same millisecond in audit_log). One flush per unload is enough.
      if (flushDoneRef.current) return;
      flushDoneRef.current = true;
      // Hydration gate (2026-11-01, Batch 6 / H-2): before the first hydrate() all
      // local state holds empty defaults — there is nothing real to flush, and
      // flushing in that state POSTs empties over saved baselines (both the
      // investigations and vitals endpoints upsert).
      if (hydratedVisitId.current !== id) return;
      // Latest-render payloads (see FlushPayloads / the mirror effect above) —
      // reading the ref at event time instead of the effect closure is what
      // unfreezes the flush from the mount render (2026-11-01, Batch 6 / H-2).
      const p = flushPayloadsRef.current;
      if (!p) return;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (invDebounceRef.current) {
        clearTimeout(invDebounceRef.current);
        invDebounceRef.current = null;
      }
      if (vitalsDebounceRef.current) {
        clearTimeout(vitalsDebounceRef.current);
        vitalsDebounceRef.current = null;
      }
      if (reviewDebounceRef.current) {
        clearTimeout(reviewDebounceRef.current);
        reviewDebounceRef.current = null;
      }
      const notesJson = p.notesJson;
      // Total-wipe guard (same as autosaveNotes): don't send an all-null-text
      // payload when the baseline carries saved text — server-side the guard
      // also blocks it, but skipping here avoids a doomed request on unload.
      const flushBaselineHadText = /"(subjective|objective|diagnosis|plan|note)":"[^"]+"/.test(lastSavedPayload.current);
      if (canEdit && notesJson !== lastSavedPayload.current && !(p.notesWipe && flushBaselineHadText)) {
        void getJwt().then((token) => {
          if (!token) return;
          void fetch(`${API_URL}/visits/${id}/notes`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: notesJson,
            keepalive: true,
            credentials: "include",
          }).catch(() => {});
        });
      }
      const invJson = p.invJson;
      // Total-wipe guard (2026-11-01, Batch 6 / H-2 — mirrors notes): a frozen-empty
      // investigations payload must never upsert-wipe a saved request.
      if (canEditInvestigations && invJson !== lastSavedInvPayload.current
          && !(p.invWipe && invBaselineHasContent(lastSavedInvPayload.current))) {
        void getJwt().then((token) => {
          if (!token) return;
          void fetch(`${API_URL}/visits/${id}/investigations`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: invJson,
            keepalive: true,
            credentials: "include",
          }).catch(() => {});
        });
      }
      const vitalsJson = p.vitalsJson;
      // Pre-hydration is covered by the flush-wide hydration gate at the top
      // (2026-11-01, Batch 6 / H-2; the per-block check was 2026-10-21,
      // post-mortem #245): before the first hydrate() the form holds empty
      // defaults and the baseline is "" — the diff check would see
      // "all-null ≠ ''" and post an all-null row when a freshly opened visit
      // is closed/navigated away.
      if (canEditVitals && vitalsJson !== lastSavedVitalsPayload.current) {
        void getJwt().then((token) => {
          if (!token) return;
          void fetch(`${API_URL}/visits/${id}/vitals`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: vitalsJson,
            keepalive: true,
            credentials: "include",
          }).catch(() => {});
        });
      }
      // Skip "nothing to save" reviews via p.reviewSkip (computed from live
      // state in the mirror — 2026-11-01, Batch 6 / H-2): !reviewNeeded &&
      // !reviewDate would 422 server-side (review_date_required); there's
      // no point sending it.
      const reviewJson = p.reviewJson;
      if (canEdit && !p.reviewSkip && reviewJson !== lastSavedReviewPayload.current) {
        void getJwt().then((token) => {
          if (!token) return;
          void fetch(`${API_URL}/visits/${id}/review`, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: reviewJson,
            keepalive: true,
            credentials: "include",
          }).catch(() => {});
        });
      }
    };
    window.addEventListener("pagehide", flushPendingSaves);
    window.addEventListener("beforeunload", flushPendingSaves);
    return () => {
      window.removeEventListener("pagehide", flushPendingSaves);
      window.removeEventListener("beforeunload", flushPendingSaves);
    };
  }, [id, canEdit, canEditInvestigations, canEditVitals]);

  // ---- Global Save: vitals + notes + investigations + review in one click ----
  // vitalValue is defined above (near vitalsPayload) so both the autosave
  // and the explicit Save use the same computation.
  //
  // Server-merge for the notes body (2026-10-05 — see post-mortem.md): an
  // explicit Save must never erase server-saved SOAP text with stale empty
  // local state. Each text field is sent only when local state is non-empty;
  // when local is empty the field is OMITTED so the server keeps its saved
  // value (the notes endpoint treats absent fields as "no change"). ICD
  // codes are always sent in full (the box owns the authoritative list, and
  // removals must propagate). A field the user deliberately cleared is still
  // preserved on the server — recoverable via copy-from-previous rather than
  // silently lost; the user directive is "saves must never wipe data".
  const mergedNotesPayload = () => {
    const p = notesPayload();
    return {
      ...(p.subjective ? { subjective: p.subjective } : {}),
      ...(p.objective ? { objective: p.objective } : {}),
      ...(p.diagnosis ? { diagnosis: p.diagnosis } : {}),
      ...(p.plan ? { plan: p.plan } : {}),
      ...(p.note ? { note: p.note } : {}),
      icd10Codes: p.icd10Codes,
    };
  };
  const saveVisit = async () => {
    if (!canEdit) return;
    // Flush any pending autosave debounces so (a) they don't race the explicit
    // Save below and (b) a just-cleared field still reaches the server (the
    // autosave sends the full payload including the cleared field; the merged
    // explicit Save omits empty fields — running the autosave first preserves
    // the deliberate clear, then the merge only skips fields already saved
    // as empty). Awaited like handleCopySelected, so both complete first.
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; await autosaveNotes(); }
    if (invDebounceRef.current) { clearTimeout(invDebounceRef.current); invDebounceRef.current = null; await autosaveInvestigations(); }
    if (vitalsDebounceRef.current) { clearTimeout(vitalsDebounceRef.current); vitalsDebounceRef.current = null; await autosaveVitals(); }
    if (reviewDebounceRef.current) { clearTimeout(reviewDebounceRef.current); reviewDebounceRef.current = null; await autosaveReview(); }
    setSaving(true);
    try {
      const notesBody = JSON.stringify(mergedNotesPayload());
      const invBody = JSON.stringify(investigationsPayload());
      const vitalsBody = vitalsPayload();
      const reviewBody = reviewPayload();

      // Save all four sections in parallel. The review PATCH is only sent
      // if a review date is set or the review is PRN (reviewNeeded=true).
      // If reviewNeeded=false && reviewDate is empty, the review is skipped
      // (the server would reject it with 422 review_date_required) — the
      // notes/vitals/investigations are still saved.
      // Vitals POST is skipped when the payload is identical to the last
      // saved baseline (2026-10-21, post-mortem #245): an unconditional POST
      // on every Save was creating a new all-null row for every visit whose
      // vitals were never touched (~1,000/month). A deliberate "Clear all"
      // still lands — the blank payload then differs from a valued baseline.
      // (2026-10-27, post-mortem #256) the API is now an UPSERT — one row
      // per visit — so even a redundant POST can no longer accumulate rows.
      const saves: Promise<unknown>[] = [
        apiFetch(`/visits/${id}/notes`, { method: "POST", body: notesBody }),
        apiFetch(`/visits/${id}/investigations`, { method: "POST", body: invBody }),
      ];
      if (vitalsBody !== lastSavedVitalsPayload.current) {
        saves.push(apiFetch(`/visits/${id}/vitals`, { method: "POST", body: vitalsBody }));
      }
      if (reviewNeeded || reviewDate) {
        saves.push(
          apiFetch(`/visits/${id}/review`, { method: "PATCH", body: reviewBody }),
        );
      }

      await Promise.all(saves);
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedPayload.current = notesBody;
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedInvPayload.current = invBody;
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedVitalsPayload.current = vitalsBody;
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      if (reviewNeeded || reviewDate) lastSavedReviewPayload.current = reviewBody;
      await refetchAndHydrate();
      toast.success("Visit saved");
    } catch (e) {
      const err = e as Error & { body?: { error?: string } };
      if (err?.body?.error === "review_date_not_working") {
        toast.error("Selected review date is not a working day or is blocked.");
      } else if (err?.body?.error === "review_date_required") {
        toast.error("Pick a review date, or tick \"Review if needed\" to skip scheduling.");
      } else {
        toast.error("Failed to save visit");
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Complete record ----
  // Saves the visit (same as Save record) then marks the appointment
  // Completed and stamps visits.checkout_at via POST /visits/:id/complete.
  // Admin-only on the server (admin_only_complete). On success, navigates back
  // to /appointments (the Morning/Evening accordions with the "Checked In"
  // tab — i.e. the checked-in list) so the user lands on the queue after
  // finishing a visit, instead of staying on the now-Completed visit page.
  const completeVisit = async () => {
    if (!canEdit) return;
    if (!isAdmin) {
      toast.error("Only admins can complete a visit.");
      return;
    }
    // Flush any pending autosave debounces before the explicit save (awaited
    // — same rationale as saveVisit above: cleared fields must land first,
    // then the server-merged Save keeps saved content).
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; await autosaveNotes(); }
    if (invDebounceRef.current) { clearTimeout(invDebounceRef.current); invDebounceRef.current = null; await autosaveInvestigations(); }
    if (vitalsDebounceRef.current) { clearTimeout(vitalsDebounceRef.current); vitalsDebounceRef.current = null; await autosaveVitals(); }
    if (reviewDebounceRef.current) { clearTimeout(reviewDebounceRef.current); reviewDebounceRef.current = null; await autosaveReview(); }
    setCompleting(true);
    try {
      // Same server-merge as Save record (see mergedNotesPayload above):
      // Complete must never erase server-saved SOAP text with stale empty
      // local state — it persists what's on screen and keeps saved content.
      const notesBody = JSON.stringify(mergedNotesPayload());
      const invBody = JSON.stringify(investigationsPayload());
      const vitalsBody = vitalsPayload();
      const reviewBody = reviewPayload();

      // 1. Save the visit (vitals + notes + investigations + review).
      // The review PATCH is only sent if a review date is set or the review
      // is PRN (reviewNeeded=true). If reviewNeeded=false && reviewDate is
      // empty, the review is skipped — notes/vitals/investigations are still
      // saved. The complete endpoint will reject if a review date is required
      // but missing (the server-side complete handler checks this).
      // Vitals POST is skipped when identical to the last-saved baseline
      // (2026-10-21, post-mortem #245) — stops the all-null row accumulation
      // on every Complete of a vitals-less visit; "Clear all" still lands.
      const saves: Promise<unknown>[] = [
        apiFetch(`/visits/${id}/notes`, { method: "POST", body: notesBody }),
        apiFetch(`/visits/${id}/investigations`, { method: "POST", body: invBody }),
      ];
      if (vitalsBody !== lastSavedVitalsPayload.current) {
        saves.push(apiFetch(`/visits/${id}/vitals`, { method: "POST", body: vitalsBody }));
      }
      if (reviewNeeded || reviewDate) {
        saves.push(
          apiFetch(`/visits/${id}/review`, { method: "PATCH", body: reviewBody }),
        );
      }

      await Promise.all(saves);
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedPayload.current = notesBody;
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedInvPayload.current = invBody;
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedVitalsPayload.current = vitalsBody;
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      if (reviewNeeded || reviewDate) lastSavedReviewPayload.current = reviewBody;

      // 2. Complete the visit (marks the appointment Completed + checkout_at).
      await apiFetch(`/visits/${id}/complete`, { method: "POST" });

      // Invalidate the appointments + dashboard queries so the destination
      // list reflects the newly-Completed appointment, then leave the visit
      // page and return to the appointments page (Morning/Evening accordions
      // with the "Checked In" tab — the checked-in list).
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Visit completed");
      navigate("/appointments");
    } catch (e) {
      const err = e as Error & { body?: { error?: string; message?: string } };
      if (err?.body?.error === "review_date_not_working") {
        toast.error("Selected review date is not a working day or is blocked.");
      } else if (err?.body?.error === "review_date_required") {
        toast.error("Pick a review date, or tick \"Review if needed\" to skip scheduling.");
      } else if (err?.body?.error === "admin_only_complete") {
        toast.error("Only admins can complete a visit.");
      } else if (err?.body?.error === "complete_requires_checkin") {
        toast.error("Visit has not been checked in yet.");
      } else if (err?.body?.error === "no_appointment") {
        toast.error("This visit has no linked appointment to complete.");
      } else {
        toast.error("Failed to complete visit");
      }
    } finally {
      setCompleting(false);
    }
  };

  if (!canView) {
    return <div className="p-6 text-muted">You don't have permission to view visits.</div>;
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-12" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const v = data?.visit;
  if (!v) {
    return <div className="p-6 text-muted">Visit not found.</div>;
  }

  // Previous visits (excluding the current one) — rows come back sorted desc
  // by check-in (GET /visits?patientId=). Feeds the "Previous visits" count
  // badge and the last-visit review strip below.
  const prevVisitRows = (prevVisitsData?.rows ?? []).filter((r) => r.id !== v.id);
  const previousVisitsCount = prevVisitRows.length;

  // Documents count — for the badge on the "Documents" button. All documents
  // saved for the patient (matching what the Documents dialog lists).
  const documentsCount = (docsData?.rows ?? []).length;

  // ---- Last-visit review strip (2026-11-03) ----
  // The patient's most recent visit BEFORE this one, the review date that
  // visit scheduled, and whether THIS visit happened before that review date
  // (came early) or after it (came late). Rows are desc by check-in, so the
  // first row on/before this visit's day (IST date strings compare
  // chronologically) is the last visit. PRN reviews (reviewNeeded=true) and
  // missing dates carry no reviewDate — the strip shows "---" for them.
  const currentVisitDay = dateIST(v.checkInAt);
  const lastVisit = prevVisitRows.find((r) => dateIST(r.checkInAt) <= currentVisitDay) ?? null;
  const lastVisitDate = lastVisit ? dateIST(lastVisit.checkInAt) : null;
  const lastReviewDate = lastVisit?.reviewDate ? dateIST(lastVisit.reviewDate) : null;
  // Day difference between this visit's day and the scheduled review day:
  // negative = came before (early — green "-"), positive = came after (late —
  // red "+"), zero = on time (green). Both parsed at local midnight; IST has
  // no DST, so the ms diff is an exact multiple of 86,400,000.
  const reviewDiffDays = lastReviewDate
    ? Math.round(
        (new Date(currentVisitDay + "T00:00:00").getTime()
          - new Date(lastReviewDate + "T00:00:00").getTime()) / 86_400_000,
      )
    : null;

  const ageYm = computeAgeYm(v.patientDob);
  const age = formatAgeLabel(ageYm);
  const ageSex = [age, v.patientGender ?? null].filter(Boolean).join(" · ");
  const note = data?.note ?? null;
  const prescription = data?.prescriptions[0] ?? null;
  const vitals = data?.vitals ?? [];

  const bmi = (() => {
    const h = parseFloat(vitalsForm.heightCm);
    const w = parseFloat(vitalsForm.weightKg);
    return h > 0 && w > 0 ? (w / ((h / 100) * (h / 100))).toFixed(1) : "";
  })();

  // ---- Printing ----
  // Builds and prints the prescription PDF. Accepts explicit review state so
  // the ReviewDateDialog's "Save & Print" callback can print with the freshly
  // chosen values before React state has flushed into this render closure.
  const buildAndPrintPrescription = async (
    rxReviewNeeded: boolean = reviewNeeded,
    rxReviewDate: string = reviewDate,
  ) => {
    // Live rows from the table first (they may be ahead of the cached
    // aggregate if a persist is still in flight); fall back to the cache.
    const meds = liveMedicines.length > 0 ? liveMedicines : prescription?.medicines ?? [];
    if (meds.length === 0) {
      toast.error("No prescription to print");
      return;
    }
    setPrinting("rx");
    try {
      const doc = await createPrescriptionPdf({
        patientName: v.patientName ?? "Patient",
        clinicUhid: v.clinicUhid ?? "",
        patientDob: v.patientDob,
        patientGender: v.patientGender,
        checkInAt: v.checkInAt,
        // Live local state — the cached note may lag the autosave debounce.
        diagnosis: diagnosis || null,
        medicines: meds as PrescriptionMedicine[],
        showComposition: printCompositionPref(),
        reviewNeeded: rxReviewNeeded,
        reviewDate: rxReviewDate ? new Date(rxReviewDate).toISOString() : null,
        doctorName: v.doctorName,
        doctorQualifications: v.doctorQualifications,
        doctorRegNumber: v.doctorRegNumber,
      });
      printPdf(doc);
    } finally {
      setPrinting(null);
    }
  };

  // Print Prescription entry point. If the visit has no review date selected
  // (reviewNeeded=false && reviewDate empty), prompt the user to pick one via
  // the ReviewDateDialog before printing — the prescription always carries a
  // review date (or PRN) line, so the doctor must decide before printing.
  // A date that IS set but lands on a blocked/off day is just as unprintable
  // (the server would 422 the save; the PDF would carry an unusable date) —
  // prompt via the same dialog, seeded with the current date so the doctor
  // sees the red warning and must pick a usable date (or PRN) to print.
  const printPrescription = async () => {
    if (!reviewNeeded && (!reviewDate || reviewDateUnavailable)) {
      setReviewDialogPrintMode("rx");
      setReviewDialogOpen(true);
      return;
    }
    await buildAndPrintPrescription();
  };

  // ReviewDateDialog "Save & Print": persist the chosen review (PATCH
  // /visits/:id/review), reflect it on the page (reviewNeeded/reviewDate
  // state, which the ReviewBar and the PDF both read), then run the print
  // that triggered the dialog (rx or summary). Shared by both print
  // entrypoints — reviewDialogPrintMode records which one to run.
  const handleReviewDialogConfirm = async (chosenNeeded: boolean, chosenDate: string) => {
    setReviewDialogBusy(true);
    try {
      // Reflect the choice on the page immediately so the ReviewBar shows it.
      setReviewNeeded(chosenNeeded);
      setReviewDate(chosenDate);

      // Persist the review so the server (and the printed PDF line) match.
      const reviewBody = JSON.stringify({
        reviewNeeded: chosenNeeded,
        reviewDate: chosenDate ? new Date(chosenDate).toISOString() : null,
      });
      await apiFetch(`/visits/${id}/review`, {
        method: "PATCH",
        body: reviewBody,
      });
      // eslint-disable-next-line react-hooks/immutability -- deliberate: last-saved baseline for autosave diff (see ref block comment)
      lastSavedReviewPayload.current = reviewBody;

      // Refresh the cached aggregate so subsequent reads see the persisted
      // review state. No need to fully re-hydrate (that would clobber local
      // edits); refetch is enough to keep the cache consistent.
      await refetch();

      setReviewDialogOpen(false);

      // Run the print that triggered the dialog, passing the freshly-chosen
      // review values explicitly so we don't depend on React state having
      // flushed into this closure.
      if (reviewDialogPrintMode === "summary") {
        await buildAndPrintSummary(chosenNeeded, chosenDate);
      } else {
        await buildAndPrintPrescription(chosenNeeded, chosenDate);
      }

      toast.success("Review date saved");
    } catch (e) {
      const err = e as Error & { body?: { error?: string; message?: string } };
      if (err?.body?.error === "review_date_not_working") {
        // Prefer the server's human message (names the block reason).
        toast.error(err.body.message ?? "Selected review date is not a working day or is blocked.");
      } else if (err?.body?.error === "review_date_required") {
        toast.error("Pick a review date, or tick \"Review if needed\" to skip scheduling.");
      } else {
        toast.error("Failed to save review date");
      }
    } finally {
      setReviewDialogBusy(false);
    }
  };

  const splitByCategory = (cat: InvestigationCategory) => {
    const testIds = new Set(selectedInv);
    return investigations
      .filter((i) => testIds.has(i.id))
      .filter((t) => classifyInvestigation(t.category) === cat);
  };

  const printInvestigations = async () => {
    if (selectedInv.length === 0) {
      toast.error("No investigations selected");
      return;
    }
    setPrinting("inv");
    try {
      const categories: { category: InvestigationCategory; tests: { id: string; name: string }[]; notes: string | null }[] = [
        { category: "Lab", tests: splitByCategory("Lab"), notes: labNotes || null },
        { category: "Radiology", tests: splitByCategory("Radiology"), notes: radiologyNotes || null },
        { category: "Electrophysiology", tests: splitByCategory("Electrophysiology"), notes: electrophysiologyNotes || null },
      ];
      if (categories.every((c) => c.tests.length === 0)) {
        toast.error("No investigations selected");
        return;
      }
      const doc = await createCombinedInvestigationPdf({
        patientName: v.patientName ?? "Patient",
        clinicUhid: v.clinicUhid ?? "",
        patientDob: v.patientDob,
        patientGender: v.patientGender,
        checkInAt: v.checkInAt,
        // Live local diagnosis (cached note may lag the autosave debounce).
        diagnosis: diagnosis || null,
        categories,
        investigationDetails,
        doctorName: v.doctorName,
        doctorRegNumber: v.doctorRegNumber,
      });
      printPdf(doc);
    } finally {
      setPrinting(null);
    }
  };

  // Builds and prints the visit summary PDF. Accepts explicit review state so
  // the ReviewDateDialog's "Save & Print" callback can print with the freshly
  // chosen values before React state has flushed into this render closure.
  // The visit summary now renders the prescription table inline on page 1
  // (after Plan) and carries the review line — so it also requires a review
  // date (or PRN) before printing.
  const buildAndPrintSummary = async (
    rxReviewNeeded: boolean = reviewNeeded,
    rxReviewDate: string = reviewDate,
  ) => {
    setPrinting("summary");
    try {
      const doc = await createVisitSummaryPdf({
        patientName: v.patientName ?? "Patient",
        clinicUhid: v.clinicUhid ?? "",
        patientDob: v.patientDob,
        patientGender: v.patientGender,
        checkInAt: v.checkInAt,
        doctorName: v.doctorName,
        doctorQualifications: v.doctorQualifications,
        doctorRegNumber: v.doctorRegNumber,
        // Live local state throughout — the cached aggregate (note,
        // investigationRequest, review) may lag the autosave debounce.
        note: {
          subjective: subjective || null,
          objective: objective || null,
          diagnosis: diagnosis || null,
          plan: plan || null,
        },
        prescription: prescription || liveMedicines.length > 0 ? {
          medicines: (liveMedicines.length > 0 ? liveMedicines : prescription?.medicines ?? []) as PrescriptionMedicine[],
          showComposition: printCompositionPref(),
          reviewNeeded: rxReviewNeeded,
          reviewDate: rxReviewDate ? new Date(rxReviewDate).toISOString() : null,
        } : null,
        investigationRequest: {
          diagnosis: diagnosis || null,
          notes: labNotes || null,
          radiologyNotes: radiologyNotes || null,
          electrophysiologyNotes: electrophysiologyNotes || null,
          selectedInvestigationIds: selectedInv,
          investigationDetails: Object.fromEntries(
            Object.entries(investigationDetails)
              .filter(([, v]) => v.regionAndSide.trim() || v.clinicalNotes.trim())
              .map(([k, v]) => [k, {
                regionAndSide: v.regionAndSide.trim(),
                clinicalNotes: v.clinicalNotes.trim(),
              }]),
          ),
        },
        investigations,
      });
      printPdf(doc);
    } finally {
      setPrinting(null);
    }
  };

  // Print Visit Summary entry point. If the visit has no review date selected
  // (reviewNeeded=false && reviewDate empty), prompt via the ReviewDateDialog
  // — the visit summary now includes the prescription table + review line,
  // so a review date (or PRN) is required before printing. A blocked/off
  // review date is treated the same as no date (see printPrescription).
  const printSummary = async () => {
    if (!reviewNeeded && (!reviewDate || reviewDateUnavailable)) {
      setReviewDialogPrintMode("summary");
      setReviewDialogOpen(true);
      return;
    }
    await buildAndPrintSummary();
  };

  // ---- Copy-from-previous (checkbox selection) ----
  const handleCopySelected = (
    src: { note: EmrNote | null; prescription: PrescriptionRow | null; vitals: VitalRow[] },
    sel: CopySelection,
  ) => {
    void (async () => {
      try {
        // Flush pending autosaves first so a debounced POST can't overwrite
        // the copied fields with stale local content a moment later.
        if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; await autosaveNotes(); }
        if (invDebounceRef.current) { clearTimeout(invDebounceRef.current); invDebounceRef.current = null; await autosaveInvestigations(); }
        if (vitalsDebounceRef.current) { clearTimeout(vitalsDebounceRef.current); vitalsDebounceRef.current = null; await autosaveVitals(); }
        if (reviewDebounceRef.current) { clearTimeout(reviewDebounceRef.current); reviewDebounceRef.current = null; await autosaveReview(); }
        if (src.note && (sel.subjective || sel.objective || sel.diagnosis || sel.icd10Codes || sel.plan || sel.note)) {
          await apiFetch(`/visits/${id}/notes`, {
            method: "POST",
            body: JSON.stringify({
              ...(sel.subjective ? { subjective: src.note.subjective } : {}),
              ...(sel.objective ? { objective: src.note.objective } : {}),
              ...(sel.diagnosis ? { diagnosis: src.note.diagnosis } : {}),
              ...(sel.icd10Codes ? { icd10Codes: src.note.icd10Codes ?? [] } : {}),
              ...(sel.plan ? { plan: src.note.plan } : {}),
              ...(sel.note ? { note: src.note.note } : {}),
            }),
          });
        }
        // Copy vitals — POST /visits/:id/vitals UPSERTS the visit's single
        // vitals row (2026-10-27, post-mortem #256 — was insert-only). We
        // copy the latest source vital row's values; if the source has no
        // vitals, nothing is sent. Height is integer-only now — truncate
        // legacy decimal values so the copy can't 400 against the integer
        // rule; BMI is recomputed from the truncated height.
        // Skip all-null source rows (2026-10-21, post-mortem #245): copying
        // a blank row would overwrite the current visit's vitals with nulls.
        if (sel.vitals && src.vitals.length > 0) {
          const sv = src.vitals.find((r) => r.tempC != null || r.bpSystolic != null || r.bpDiastolic != null
            || r.pulse != null || r.respRate != null || r.spO2 != null
            || r.heightCm != null || r.weightKg != null) ?? null;
          if (sv) {
            const h = sv.heightCm != null ? Math.trunc(sv.heightCm) : null;
            const w = sv.weightKg;
            const bmi = h && w && h > 0 ? parseFloat((w / ((h / 100) * (h / 100))).toFixed(1)) : sv.bmi;
            await apiFetch(`/visits/${id}/vitals`, {
              method: "POST",
              body: JSON.stringify({
                tempC: sv.tempC,
                bpSystolic: sv.bpSystolic,
                bpDiastolic: sv.bpDiastolic,
                pulse: sv.pulse,
                respRate: sv.respRate,
                spO2: sv.spO2,
                heightCm: h,
                weightKg: sv.weightKg,
                bmi,
              }),
            });
          }
        }
        if (sel.prescription && src.prescription) {
          // Taper JSONB is copied VERBATIM (user decision 2026-10-30): the
          // source visit's historical fromDate values are preserved so the
          // doctor sees what the old protocol was and edits dates by hand.
          // Only prescription-template insertion rebases to today — never copy.
          await apiFetch(`/visits/${id}/prescriptions`, {
            method: "POST",
            body: JSON.stringify({
              medicines: src.prescription.medicines.map((m, i) => ({
                drugId: m.drugId,
                name: m.name,
                composition: m.composition,
                formulation: m.formulation,
                strength: m.strength,
                frequency: m.frequency,
                taper: m.taper,
                duration: m.duration,
                instructions: m.instructions,
                serialNo: i + 1,
              })),
            }),
          });
        }
        await refetchAndHydrate();
        toast.success("Copied selected items");
      } catch {
        toast.error("Failed to copy");
      }
    })();
  };

  const setVital = (k: VitalKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    // Integer vitals (BP/Pulse/Resp/SpO₂/Height) reject decimal input — keep only
    // digits up to (but excluding) the first decimal point the browser may
    // have accepted. Temp/Weight keep the raw value (parseFloat later).
    const raw = e.target.value;
    setVitalsForm({ ...vitalsForm, [k]: INTEGER_VITAL_KEYS.has(k) ? raw.replace(/\..*$/, "") : raw });
  };

  const clearVitals = () => {
    setVitalsForm(EMPTY_VITALS);
  };

  return (
    <div className="p-4 space-y-3">
      {/* Header row — photo left, name/details shifted right */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft size={18} /></Button>
          {/* Patient photo — clickable: opens the big view + change/remove. */}
          <button
            type="button"
            onClick={() => setPhotoViewerOpen(true)}
            className="group relative h-[74px] w-[58px] shrink-0 overflow-hidden rounded-md border border-cream-200 bg-cream-50 transition-colors hover:border-maroon-600 dark:border-dark-700 dark:bg-dark-800 dark:hover:border-amber-500"
            title={patientPhotoKey ? "View patient photo" : "Patient photo"}
          >
            {patientPhotoKey && photoUrl ? (
              <img src={photoUrl} alt="Patient photo" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-muted">
                <User size={22} />
                {(isAdmin || permissions.has("photos.create")) && <span className="text-[8px] leading-none">Add photo</span>}
              </div>
            )}
          </button>
          <div>
            <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">
              <button
                type="button"
                onClick={() => navigate(`/patients/${v.patientId}`)}
                className="text-left underline-offset-2 hover:underline focus:underline"
                title="View patient details"
              >
                {v.patientName ?? "Patient"}
              </button>
            </h1>
            <div className="text-sm text-muted">
              <button
                type="button"
                onClick={() => navigate(`/patients/${v.patientId}`)}
                className="text-left font-mono underline-offset-2 hover:underline focus:underline"
                title="View patient details"
              >
                {v.clinicUhid}
              </button>
              {" · "}
              {ageSex} · Visit {fmtDate(v.checkInAt)} · {v.doctorName ?? "—"}
            </div>
          </div>
        </div>
        {/* Right header column: buttons row on top; the last-visit review
            strip sits DIRECTLY below the buttons, right-aligned, inside the
            SAME header height — the UHID·age details line on the left is
            tall enough to cover both rows, so this adds no header height. */}
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-muted">
              {autosaveState === "saving" && <><LoaderCircle size={12} className="animate-spin" /> Saving…</>}
              {autosaveState === "saved" && <><Check size={12} className="text-green-700" /> Saved</>}
            </div>
            <Button variant="outline" size="sm" onClick={() => setPrevOpen(true)} className="relative">
              <RotateCcwClock size={14} className="mr-1" /> Previous visits
              {previousVisitsCount > 0 && (
                <Badge
                  className="absolute -right-2 -top-2 h-5 min-w-5 justify-center px-1.5 text-[10px] leading-none"
                >
                  {previousVisitsCount}
                </Badge>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <FileText size={14} className="mr-1" /> Referral Letter
                  <ChevronDown size={12} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/referrals/to/new?visitId=${v.id}`)}>Referral To</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/referrals/reply/new?visitId=${v.id}`)}>Referral Reply</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={() => setDocsOpen(true)} className="relative">
              <FileText size={14} className="mr-1" /> Documents
              {documentsCount > 0 && (
                <Badge
                  className="absolute -right-2 -top-2 h-5 min-w-5 justify-center px-1.5 text-[10px] leading-none"
                >
                  {documentsCount}
                </Badge>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  {printing ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <Printer size={14} className="mr-1" />}
                  Print
                  <ChevronDown size={12} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={printPrescription}>Prescription</DropdownMenuItem>
                <DropdownMenuItem onClick={printInvestigations}>Investigations</DropdownMenuItem>
                <DropdownMenuItem onClick={printSummary}>Visit Summary</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {canEdit && (
              <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={saveVisit} disabled={saving || completing}>
                {saving ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                Save record
              </Button>
            )}
            {canEdit && isAdmin && (
              <Button size="sm" className="bg-green-700 hover:bg-green-800 text-cream-50" onClick={completeVisit} disabled={saving || completing}>
                {completing ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <CircleCheck size={14} className="mr-1" />}
                Complete record
              </Button>
            )}
          </div>

          {/* Last-visit review strip (2026-11-03) — the patient's most recent
              visit BEFORE this one, the review date that visit scheduled, and
              whether THIS visit came before that review date (green "-") or
              after it (red "+"). No previous visit, or the last visit carried no
              review date (PRN / never set) → "---" placeholders.
              self-stretch + justify-start + pl-2: the strip spans the full
              right-column width and its first label starts directly below the
              left edge of the "Previous visits" button (the buttons row's
              leading zero-width autosave indicator + gap-2 puts that edge 8px
              in — pl-2 matches it; alignment holds for the idle state). */}
          <div className="flex flex-wrap items-center justify-start self-stretch gap-x-3 gap-y-0.5 pl-2 text-xs text-muted">
            <span>
              Last visit date:{" "}
              <span className="font-semibold text-ink dark:text-cream-50">
                {lastVisitDate ? fmtDatePdf(lastVisitDate) : "---"}
              </span>
            </span>
            <span>
              Review date:{" "}
              <span className="font-semibold text-ink dark:text-cream-50">
                {lastReviewDate ? fmtDatePdf(lastReviewDate) : "---"}
              </span>
            </span>
            <span>
              Time of review:{" "}
              {reviewDiffDays == null ? (
                <span className="font-semibold text-ink dark:text-cream-50">---</span>
              ) : reviewDiffDays < 0 ? (
                <span className="font-semibold text-green-700 dark:text-green-400">
                  - {Math.abs(reviewDiffDays)} day{Math.abs(reviewDiffDays) === 1 ? "" : "s"} before review date
                </span>
              ) : reviewDiffDays === 0 ? (
                <span className="font-semibold text-green-700 dark:text-green-400">- on review date</span>
              ) : (
                <span className="font-semibold text-red-600 dark:text-red-400">
                  + {reviewDiffDays} day{reviewDiffDays === 1 ? "" : "s"} after review date
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Vitals bar — blank-by-default inputs, integer steppers.
          Indented on large screens to align with the patient name (the photo
          column occupies the space where the bar used to start). */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-cream-200 bg-cream-50/50 px-3 py-2 dark:border-dark-700 dark:bg-dark-800/50 lg:ml-[122px]">
        <VitalField label="Temp (°C)" field="tempC" value={vitalsForm.tempC} onChange={setVital("tempC")} placeholder="98.6" canEdit={canEdit} />
        <div className="flex items-end gap-1">
          <VitalField label="BP Sys" field="bpSystolic" value={vitalsForm.bpSystolic} onChange={setVital("bpSystolic")} placeholder="120" canEdit={canEdit} stepper step={1} anchor={120} />
          <span className="pb-2 text-muted">/</span>
          <VitalField label="Dia" field="bpDiastolic" value={vitalsForm.bpDiastolic} onChange={setVital("bpDiastolic")} placeholder="80" canEdit={canEdit} stepper step={1} anchor={80} />
        </div>
        <VitalField label="Pulse" field="pulse" value={vitalsForm.pulse} onChange={setVital("pulse")} placeholder="72" canEdit={canEdit} stepper step={1} anchor={72} />
        <VitalField label="Resp" field="respRate" value={vitalsForm.respRate} onChange={setVital("respRate")} placeholder="16" canEdit={canEdit} stepper step={1} anchor={16} />
        <VitalField label="SpO₂ %" field="spO2" value={vitalsForm.spO2} onChange={setVital("spO2")} placeholder="99" canEdit={canEdit} stepper step={1} anchor={99} />
        <VitalField label="Height cm" field="heightCm" value={vitalsForm.heightCm} onChange={setVital("heightCm")} placeholder="150" canEdit={canEdit} stepper step={1} anchor={150} />
        <VitalField label="Weight kg" field="weightKg" value={vitalsForm.weightKg} onChange={setVital("weightKg")} placeholder="70" canEdit={canEdit} stepper step={1} anchor={70} />
        <div className="pb-1 text-sm">
          <span className="text-xs text-muted">BMI </span>
          <span className="font-semibold text-ink dark:text-cream-50">{bmi || "—"}</span>
        </div>
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={clearVitals} className="h-8 px-2 text-xs text-muted hover:text-ink dark:hover:text-cream-50">
            <X size={14} className="mr-1" /> Clear all
          </Button>
        )}
        {vitals[0] && (
          <div className="ml-auto pb-1 text-xs text-muted">Recorded {fmtDate(vitals[0].recordedAt)}</div>
        )}
      </div>

      {/* Three columns — each 37.5vh with internal scroll */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <VisitDetailsColumn
          subjective={subjective}
          objective={objective}
          diagnosis={diagnosis}
          onSubjective={setSubjective}
          onObjective={setObjective}
          onDiagnosis={setDiagnosis}
        />
        <CodesPlanColumn
          diagnosis={diagnosis}
          icd10Codes={icd10Codes}
          onIcd10Codes={setIcd10Codes}
          plan={plan}
          onPlan={setPlan}
          noteText={noteText}
          onNoteText={setNoteText}
          patientContext={{
            age: ageYm ? ageYm.years : undefined,
            sex: v.patientGender ?? undefined,
          }}
        />
        <InvestigationsColumn
          selected={selectedInv}
          onSelected={setSelectedInv}
          labNotes={labNotes}
          radiologyNotes={radiologyNotes}
          electrophysiologyNotes={electrophysiologyNotes}
          onLabNotes={setLabNotes}
          onRadiologyNotes={setRadiologyNotes}
          onElectrophysiologyNotes={setElectrophysiologyNotes}
          investigationDetails={investigationDetails}
          onInvestigationDetails={setInvestigationDetails}
          investigations={investigations}
        />
      </div>

      {/* Prescription — full width below */}
      <PrescriptionTable
        visit={v}
        prescription={prescription}
        note={note}
        hydrateSignal={hydrateSignal}
        onMedicinesChange={setLiveMedicines}
        liveDiagnosis={diagnosis || null}
        liveReviewNeeded={reviewNeeded}
        liveReviewDate={reviewDate ? new Date(reviewDate).toISOString() : null}
        liveReviewDateUnavailable={reviewDateUnavailable}
        onRequestReviewDate={() => {
          setReviewDialogPrintMode("rx");
          setReviewDialogOpen(true);
        }}
        reviewNeeded={reviewNeeded}
        reviewDate={reviewDate}
        onReviewNeeded={setReviewNeeded}
        onReviewDate={setReviewDate}
      />

      {/* Review */}
      <ReviewBar
        visitId={v.id}
        reviewNeeded={reviewNeeded}
        reviewDate={reviewDate}
        onReviewNeeded={setReviewNeeded}
        onReviewDate={setReviewDate}
      />

      <DocumentViewer
        open={docsOpen}
        onOpenChange={setDocsOpen}
        patientId={v.patientId}
        visitId={v.id}
      />

      <PatientPhotoViewerDialog
        open={photoViewerOpen}
        onOpenChange={setPhotoViewerOpen}
        patientId={v.patientId}
        patientName={v.patientName}
        photoR2Key={patientPhotoKey}
        onPhotoChanged={() => refetch()}
      />

      <PreviousVisitsDrawer
        open={prevOpen}
        onOpenChange={setPrevOpen}
        patientId={v.patientId}
        currentVisitId={v.id}
        onCopySelected={handleCopySelected}
      />

      <ReviewDateDialog
        open={reviewDialogOpen}
        onOpenChange={setReviewDialogOpen}
        reviewNeeded={reviewNeeded}
        reviewDate={reviewDate}
        onConfirm={handleReviewDialogConfirm}
        busy={reviewDialogBusy}
      />
    </div>
  );
}

function VitalField({
  label, field, value, onChange, placeholder, canEdit, stepper, step = 1, anchor,
}: {
  label: string;
  field: VitalKey;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  canEdit?: boolean;
  stepper?: boolean;
  step?: number;
  anchor?: number;
}) {
  // Integer stepping: first press on an empty field seeds the anchor value
  // (BP 120/80, Pulse 72, Resp 16, SpO2 99, Height 150, Weight 70), then ±step.
  // Integer vitals (BP/Pulse/Resp/SpO₂/Height) round to whole numbers; Temp/
  // Weight keep one decimal place.
  const nudge = (dir: 1 | -1) => {
    const current = parseFloat(value);
    const isInt = INTEGER_VITAL_KEYS.has(field);
    const next = Number.isFinite(current) && value !== ""
      ? isInt
        ? Math.round(current + dir * step)
        : Math.round((current + dir * step) * 10) / 10
      : (anchor ?? 0);
    onChange({ target: { value: String(next) } } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <div className="space-y-0.5">
      <Label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted">
        {label}
      </Label>
      <div className="flex items-center gap-0.5">
        {stepper && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Decrease ${label}`}
            disabled={!canEdit}
            onClick={() => nudge(-1)}
            className="flex h-8 w-5 items-center justify-center rounded-md border border-input text-muted hover:bg-cream-100 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-dark-700"
          >
            <ChevronDown size={12} />
          </button>
        )}
        <Input
          type="number"
          step={stepper ? "1" : "0.1"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={!canEdit}
          className="h-8 w-20 text-center"
        />
        {stepper && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Increase ${label}`}
            disabled={!canEdit}
            onClick={() => nudge(1)}
            className="flex h-8 w-5 items-center justify-center rounded-md border border-input text-muted hover:bg-cream-100 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-dark-700"
          >
            <ChevronUp size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
